import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";

import {
  BarOptionDefinition,
  Loader,
  MenuOptionSpec,
  NotificationStack,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";

import { BulkArchiveUserModal } from "@components/common/bulk-archive-user-modal";
import { BulkRemoveUserModal } from "@components/common/bulk-remove-user-modal";
import { RecordStatus } from "@components/custom/dropdowns";
import { ChangePasswordModal } from "@components/modals";
import {
  trackLearnersArchived,
  trackLearnersReactivated,
} from "@lib/event-trackers";
import { useArchiveUserEnabled, useFeatures } from "@queries";
import { useContainer } from "@src/contexts/container-context";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import { CareerPlansModal } from "@views/classrooms/details/modals/career-plans.modal";
import { getUserLabelsByProduct } from "@views/classrooms/details/utils/labels";
import { getColumnsDefinitionByProduct } from "@views/classrooms/details/utils/user-table.columns";
import {
  DataTableColumnDefinition,
  getUserDetailPathConfig,
} from "@views/users/list/data-table-column-definition";
import { UpdatePasswordSuccess } from "./menu-builder/utils/notification.constants";

export function ClassroomUsersMasteryTable({
  data,
  responsiveStyles,
  getUsers,
  readOnly,
  classroom,
  showSuccessNotification,
  productId,
  zeroStateMessage,
  statusFilter = RecordStatus.Active,
}) {
  const features = useFeatures();
  const { editableUsers } = features || {};
  const archiveUserEnabled = useArchiveUserEnabled(classroom?.clientCode);
  const canArchive = archiveUserEnabled && !classroom.isProfessionalLearning;
  const isTrek = productId === Product.TRK;
  const userLabels = getUserLabelsByProduct(productId);
  const columnsDefinition = getColumnsDefinitionByProduct(productId);
  const { userService } = useContainer();
  const breadcrumbLabel = "Classroom";
  const isAllStatus = statusFilter === RecordStatus.All;
  const isActiveStatus = statusFilter === RecordStatus.Active;
  const isArchivedStatus = statusFilter === RecordStatus.Archived;
  const hasBulkActions = !readOnly && !isAllStatus;
  const selectedColumn = hasBulkActions
    ? [new Column("select", undefined, ColumnType.SELECTION)]
    : [];
  const {
    classroomId,
    clientCode,
    clientName,
    name: classroomName,
    isProfessionalLearning,
  } = classroom;

  const [showRemoveFromClassroom, setShowRemoveFromClassroom] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveList, setArchiveList] = useState([]);
  const [selectedTraineeList, setSelectedTraineeList] = useState([]);
  const [notifications, setNotifications] = useState();
  const [loading, setLoading] = useState();

  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const errorIcon = ["fa-solid", "triangle-exclamation"];
  const successIcon = ["fa-solid", "circle-check"];

  const [careerPlanUser, setCareerPlanUser] = useState(null);

  const columns = useMemo(() => {
    const columns = [
      ...selectedColumn,
      DataTableColumnDefinition.AVATAR,
      DataTableColumnDefinition.LAST_NAME(
        isProfessionalLearning
          ? undefined
          : getUserDetailPathConfig({
              breadcrumbLabel,
              classroomId,
              product: productId,
            })
      ),
      DataTableColumnDefinition.FIRST_NAME(
        isProfessionalLearning
          ? undefined
          : getUserDetailPathConfig({
              breadcrumbLabel,
              classroomId,
              product: productId,
            })
      ),
      new Column(
        "username",
        "Username",
        ColumnType.BASIC,
        new ColumnOptions({ sortingEnabled: true, overflowEnabled: true })
      ),
      DataTableColumnDefinition.EMAIL,
      ...(columnsDefinition || []),
    ];

    return columns.map((c) =>
      c instanceof Function
        ? c({
            onClick: (userId) => {
              const trainee = data.find((u) => u.userId === userId);
              setCareerPlanUser(trainee);
            },
          })
        : c
    );
  }, [selectedColumn, columnsDefinition, data, breadcrumbLabel, classroomId]);

  const onCloseNotification = (id) => {
    setNotifications((n) => n.filter((notification) => notification.id !== id));
  };

  const showNotification = (type, message, icon) => {
    const notification = { type, message, icon, id: crypto.randomUUID() };
    setNotifications((n) => {
      const notificationsUpdated = n ?? [];
      return [...notificationsUpdated, notification];
    });
  };

  const onRemoveLearnerFromClassroom = (traineesToRemove) => {
    setSelectedTraineeList(traineesToRemove);
    setShowRemoveFromClassroom(true);
  };

  const onArchive = (trainees) => {
    const targets = (trainees ?? []).filter((u) => !u.isArchived);
    if (targets.length === 0) {
      return;
    }

    setArchiveList(targets);
    setShowArchive(true);
  };

  const onUnarchive = async (trainees) => {
    const targets = (trainees ?? []).filter((u) => u.isArchived);
    if (targets.length === 0) {
      return;
    }

    try {
      await userService.unarchiveUsers(
        targets.map((u) => u.userId),
        clientCode
      );
      trackLearnersReactivated(targets, { batchId: crypto.randomUUID() });
      showNotification(
        NotificationType.success,
        targets.length > 1 ? (
          <div>
            <b>{targets.length}</b> {getLearnerTerm({ plural: true })} have been
            unarchived.
          </div>
        ) : (
          <div>
            <b>
              {targets[0].firstName} {targets[0].lastName}
            </b>{" "}
            has been unarchived.
          </div>
        ),
        successIcon
      );
      setSelectedTraineeList([]);
      await getUsers();
    } catch {
      showNotification(
        NotificationType.error,
        "Something went wrong. Please try again.",
        errorIcon
      );
    } finally {
      setTimeout(() => setNotifications(), 10000);
    }
  };

  const onUpdatePassword = (traineesToUpdate) => {
    setSelectedTraineeList(traineesToUpdate);
    setOpenPasswordModal(true);
  };

  const onPasswordUpdatedHandler = (response) => {
    setOpenPasswordModal();
    onRowRemoved();
    showSuccessNotification(UpdatePasswordSuccess(response));
  };

  const onResendInvite = async (traineesToResend) => {
    const userIds = traineesToResend.map((user) => user.userId);
    await inviteRequest(userIds);
  };

  const getTraineeName = useCallback(
    (userId) => {
      const user = data.find((u) => u.id === +userId);
      const { firstName, lastName } = user ?? {};
      return { firstName, lastName };
    },
    [data]
  );

  const inviteRequest = async (selectedTrainees) => {
    setLoading(true);
    try {
      const inviteResponse = await userService.sendTrekInvites(
        selectedTrainees,
        classroomId
      );
      const failures = inviteResponse?.failures?.length;
      const successes = inviteResponse?.successes?.length;

      if (failures === 1) {
        const { firstName, lastName } = getTraineeName(
          inviteResponse.failures[0]
        );

        showNotification(
          NotificationType.error,
          `${firstName} ${lastName} is missing an email and we could not send an invite.`,
          errorIcon
        );
      } else if (failures > 1) {
        showNotification(
          NotificationType.error,
          `${failures} invites failed to send due to missing emails.`,
          errorIcon
        );
      }

      if (successes === 1) {
        const { firstName, lastName } = getTraineeName(
          inviteResponse.successes[0]
        );

        showNotification(
          NotificationType.success,
          `${firstName} ${lastName} has been invited to ${clientName}`,
          successIcon
        );
      } else if (successes > 1) {
        showNotification(
          NotificationType.success,
          `${successes} users have been invited to ${clientName}.`,
          successIcon
        );
      }
    } catch {
      showNotification(
        NotificationType.error,
        `An error has occured.`,
        errorIcon
      );
    } finally {
      setLoading();
      setTimeout(() => {
        setNotifications();
      }, 10000);
    }
  };

  const bottomBarOptions = useMemo(() => {
    const options = [];
    if (!hasBulkActions) {
      return options;
    }

    options.push(
      new BarOptionDefinition(onRemoveLearnerFromClassroom, {
        text: "Remove",
        icon: ["fa-light", "fa-xmark"],
        destructive: true,
      })
    );

    if (isActiveStatus) {
      if (isTrek) {
        options.push(
          new BarOptionDefinition(onResendInvite, {
            text: "Send Invitation",
            icon: ["fa-light", "fa-paper-plane"],
          })
        );
      }

      if (!isProfessionalLearning) {
        options.push(
          new BarOptionDefinition(onUpdatePassword, {
            text: "Change Password",
            icon: ["fa-light", "fa-key"],
            destructive: false,
          })
        );
      }

      if (canArchive) {
        options.push(
          new BarOptionDefinition(onArchive, {
            text: "Archive",
            icon: ["fa-light", "box-archive"],
            destructive: false,
          })
        );
      }
    }

    if (canArchive && isArchivedStatus) {
      options.push(
        new BarOptionDefinition(onUnarchive, {
          text: "Unarchive",
          icon: ["fa-light", "box-open"],
          destructive: false,
        })
      );
    }

    return options;
  }, [
    hasBulkActions,
    isTrek,
    isActiveStatus,
    isArchivedStatus,
    isProfessionalLearning,
    canArchive,
  ]);

  const actionMenuOptions = useMemo(
    () =>
      [
        {
          condition: true,
          spec: new MenuOptionSpec(
            "Remove",
            ["fa-light", "fa-user-times"],
            (_id, user) => onRemoveLearnerFromClassroom([user]),
            "remove_from_classroom"
          ),
        },
        {
          condition:
            !isProfessionalLearning && editableUsers?.canEditSTUDENTUser,
          spec: new MenuOptionSpec(
            "Change Password",
            ["fa-light", "fa-key"],
            (_id, user) => onUpdatePassword([user]),
            "change_password"
          ),
        },
        {
          condition: editableUsers?.canEditSTUDENTUser && isTrek,
          spec: new MenuOptionSpec(
            "Resend Invite",
            ["fa-light", "fa-paper-plane"],
            (_id, user) => onResendInvite([user]),
            "resend_invite"
          ),
        },
        {
          condition: canArchive,
          spec: new MenuOptionSpec(
            "Archive",
            ["fa-light", "box-archive"],
            (_id, user) => onArchive([user]),
            "archive"
          ),
        },
        {
          condition: canArchive,
          spec: new MenuOptionSpec(
            "Unarchive",
            ["fa-light", "box-open"],
            (_id, user) => onUnarchive([user]),
            "unarchive"
          ),
        },
      ]
        .filter(({ condition }) => !readOnly && condition)
        .map(({ spec }) => spec),
    [editableUsers, isTrek, isProfessionalLearning, readOnly, canArchive]
  );

  const anonymousHiddenMenuOptions = new Set(["edit", "change_password"]);
  const archivedAllowedMenuOptions = new Set(["unarchive"]);
  const filterRowMenuOptions = (rowData, menuOptions) => {
    if (!menuOptions) {
      return [];
    }

    let options = menuOptions;
    if (rowData?.isAnonymous) {
      options = options.filter(
        (opt) => !anonymousHiddenMenuOptions.has(opt?.id)
      );
    }
    if (rowData?.isArchived) {
      options = options.filter((opt) =>
        archivedAllowedMenuOptions.has(opt?.id)
      );
    } else {
      options = options.filter((opt) => opt?.id !== "unarchive");
    }
    return options;
  };

  const onRowRemoved = () => {
    getUsers();
    setSelectedTraineeList([]);
  };

  return (
    <>
      {loading && <Loader overlay />}
      <DataTable
        className={clsx(
          "classroom-trainees-mastery-table",
          hasBulkActions && "selection",
          Product.TS == productId && "ts",
          Product.TRK == productId && "trek",
          responsiveStyles
        )}
        bottomBarOptions={bottomBarOptions}
        columns={columns}
        data={data}
        keyFields={["userId"]}
        responsiveStyles={responsiveStyles}
        tableContentName={userLabels}
        defaultSortPropName="isAnonymous"
        defaultSortDirection={SortDirection.ASCENDING}
        actionMenuOptions={actionMenuOptions}
        isMenuActionVisible={filterRowMenuOptions}
        zeroStateMessage={zeroStateMessage}
      />
      <BulkRemoveUserModal
        onCancel={() => setShowRemoveFromClassroom()}
        show={showRemoveFromClassroom}
        deleteList={selectedTraineeList}
        onRowRemoved={onRowRemoved}
        classroomName={classroomName}
        classroomId={classroomId}
        userLabel={userLabels}
        isMultiProductClassroom={classroom.products?.length > 1}
        isProfessionalLearning={isProfessionalLearning}
      />
      <BulkArchiveUserModal
        open={showArchive}
        learners={archiveList}
        orgCode={clientCode}
        onCancel={() => setShowArchive(false)}
        onArchived={() => {
          trackLearnersArchived(archiveList, {
            source: "tab",
            batchId: crypto.randomUUID(),
          });
          setArchiveList([]);
          onRowRemoved();
        }}
      />
      <ChangePasswordModal
        open={openPasswordModal}
        userIds={selectedTraineeList.map((u) => u.userId)}
        onCancel={() => setOpenPasswordModal()}
        onPasswordChanged={onPasswordUpdatedHandler}
      />
      <CareerPlansModal
        onClose={() => setCareerPlanUser()}
        classroom={classroom}
        trainee={careerPlanUser}
      />
      <div className="notification-container">
        {notifications?.length > 0 && (
          <NotificationStack
            notifications={notifications}
            onClose={onCloseNotification}
            closable
          />
        )}
      </div>
    </>
  );
}
